import { useCallback } from 'react';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

interface ResourceClickEvent {
  resourceTitle: string;
  resourceCategory: string;
  resourceType: 'consumer' | 'professional';
  link: string;
}

interface SearchEvent {
  query: string;
  resultsCount: number;
  selectedTab: 'consumer' | 'professional';
}

interface FilterEvent {
  filterType: 'category' | 'audience';
  filterValue: string;
  selectedTab: 'consumer' | 'professional';
}

// This hook provides a simple interface for tracking analytics events
// It can be easily connected to Google Analytics, Adobe Analytics, or other services
export const useAnalytics = () => {
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    // Send to Google Analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      });
    }
    
    // Log for debugging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics Event:', event);
    }
    
    // Store in localStorage for demonstration purposes
    try {
      const existingEvents = JSON.parse(localStorage.getItem('mad_analytics') || '[]');
      existingEvents.push({
        ...event,
        timestamp: new Date().toISOString(),
      });
      
      // Keep only last 100 events
      if (existingEvents.length > 100) {
        existingEvents.splice(0, existingEvents.length - 100);
      }
      
      localStorage.setItem('mad_analytics', JSON.stringify(existingEvents));
    } catch (error) {
      console.warn('Failed to store analytics event:', error);
    }
  }, []);

  const trackResourceClick = useCallback((event: ResourceClickEvent) => {
    trackEvent({
      category: 'Resource',
      action: 'click',
      label: `${event.resourceType}:${event.resourceCategory}:${event.resourceTitle}`,
    });
  }, [trackEvent]);

  const trackSearch = useCallback((event: SearchEvent) => {
    trackEvent({
      category: 'Search',
      action: 'search',
      label: `${event.selectedTab}:${event.query}`,
      value: event.resultsCount,
    });
  }, [trackEvent]);

  const trackFilter = useCallback((event: FilterEvent) => {
    trackEvent({
      category: 'Filter',
      action: event.filterType,
      label: `${event.selectedTab}:${event.filterValue}`,
    });
  }, [trackEvent]);

  const trackTabChange = useCallback((tabName: 'consumer' | 'professional') => {
    trackEvent({
      category: 'Navigation',
      action: 'tab_change',
      label: tabName,
    });
  }, [trackEvent]);

  const trackDownload = useCallback((resourceTitle: string, resourceType: 'consumer' | 'professional') => {
    trackEvent({
      category: 'Download',
      action: 'resource_download',
      label: `${resourceType}:${resourceTitle}`,
    });
  }, [trackEvent]);

  const trackPageView = useCallback((pageName: string, additionalData?: Record<string, any>) => {
    trackEvent({
      category: 'Page',
      action: 'view',
      label: pageName,
    });
    
    // Log additional page data
    if (additionalData) {
      console.log('Page View Data:', { pageName, ...additionalData });
    }
  }, [trackEvent]);

  // v1.1 spec §4.2 — GA4 Key Events
  const trackGenerateLead = useCallback((service: string) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'generate_lead', { service });
    }
    trackEvent({ category: 'Conversion', action: 'generate_lead', label: service });
  }, [trackEvent]);

  const trackPhoneClick = useCallback((location: 'header' | 'footer' | 'contact_page') => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'phone_click', { location });
    }
    trackEvent({ category: 'Engagement', action: 'phone_click', label: location });
  }, [trackEvent]);

  const trackServiceCardClick = useCallback((service: string) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'service_card_click', { service });
    }
    trackEvent({ category: 'Engagement', action: 'service_card_click', label: service });
  }, [trackEvent]);

  // Helper function to get analytics data (for debugging/admin purposes)
  const getAnalyticsData = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem('mad_analytics') || '[]');
    } catch (error) {
      console.warn('Failed to retrieve analytics data:', error);
      return [];
    }
  }, []);

  return {
    trackEvent,
    trackResourceClick,
    trackSearch,
    trackFilter,
    trackTabChange,
    trackDownload,
    trackPageView,
    trackGenerateLead,
    trackPhoneClick,
    trackServiceCardClick,
    getAnalyticsData,
  };
};

export default useAnalytics; 